import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Row,
  Switch,
  Tabs,
} from 'antd';
import type { AxiosResponse } from 'axios';
import { SearchOutlined } from '@ant-design/icons';
import PenIcon from 'assets/icons/pen.svg';
import TrashIcon from 'assets/icons/trash.svg';
import ControlAreaService, {
  ControlAreaPayload,
  ControlAreaResponse,
} from '../services/controlAreaService';
import AttributeService from 'services/AttributeService';

const normalizeText = (value?: string) => (value ?? '').toLocaleLowerCase('tr-TR');
type StatusTabKey = 'active' | 'passive';

const ControlAreaList: React.FC = () => {
  const [data, setData] = useState<ControlAreaResponse[]>([]);
  const [filtered, setFiltered] = useState<ControlAreaResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<ControlAreaResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [attributeOptions, setAttributeOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedAttributeIds, setSelectedAttributeIds] = useState<number[]>([]);
  const [pendingAttributeIds, setPendingAttributeIds] = useState<number[]>([]);
  const [statusTab, setStatusTab] = useState<StatusTabKey>('active');
  const [statusUpdatingId, setStatusUpdatingId] = useState<number | null>(null);
  const [form] = Form.useForm<ControlAreaPayload>();
  const { Text } = Typography;
  const isActiveFilter = statusTab === 'active';

  const applySearch = (list: ControlAreaResponse[], term: string) => {
    const normalizedTerm = normalizeText(term);
    if (!normalizedTerm.length) {
      return list.slice();
    }
    return list.filter((item) => {
      const matchesControlAreaName = normalizeText(item.name).includes(normalizedTerm);
      const matchesAttributeName = (item.attributes || []).some((attribute) =>
        normalizeText(attribute?.name).includes(normalizedTerm)
      );

      return matchesControlAreaName || matchesAttributeName;
    });
  };

  const fetchControlAreas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ControlAreaService.listControlAreas(isActiveFilter);
      const list = Array.isArray(response.data) ? response.data : [];
      setData(list);
      setFiltered(list);
    } catch (error) {
      message.error('Parametre tipleri - özellikleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [isActiveFilter]);

  useEffect(() => {
    fetchControlAreas();
  }, [fetchControlAreas]);

  useEffect(() => {
    let mounted = true;
    AttributeService.getAllActive()
      .then((response: AxiosResponse<any[]>) => {
        if (!mounted) return;
        const list = Array.isArray(response.data) ? response.data : [];
        const flattened: Array<{ value: number; label: string }> = [];
        const traverse = (items: any[]) => {
          items.forEach((item) => {
            if (!item) return;
            const isActive = item?.isActive ?? item?.active ?? true;
            if (!item.isGroup && isActive) {
              const id = Number(item.id);
              if (Number.isFinite(id) && !flattened.some((existing) => existing.value === id)) {
                const name = typeof item.name === 'string' && item.name.trim().length ? item.name : `#${id}`;
                flattened.push({ value: id, label: name });
              }
            }
            if (Array.isArray(item.children) && item.children.length) {
              traverse(item.children);
            }
          });
        };
        traverse(list);
        setAttributeOptions(flattened);
      })
      .catch(() => {
        if (mounted) {
          setAttributeOptions([]);
        }
        message.error('Parametreler yüklenemedi.');
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const bySearch = applySearch(data, searchTerm);
    const applyAttributeFilter = (list: ControlAreaResponse[]) => {
      if (!selectedAttributeIds.length) return list;
      return list.filter((item) => {
        const attrIds = (item?.attributes || []).map((attr: any) => Number(attr?.id)).filter(Number.isFinite);
        return attrIds.some((id) => selectedAttributeIds.includes(id));
      });
    };
    setFiltered(applyAttributeFilter(bySearch));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, data, selectedAttributeIds]);

  const handleOpenDrawer = (record?: ControlAreaResponse) => {
    if (record) {
      setEditing(record);
      form.setFieldsValue({
        name: (record.name || '').toLocaleUpperCase('tr-TR'),
        isActive: record.isActive !== false,
        isOther: record.isOther === true,
        attributeIds: Array.isArray(record.attributes) ? record.attributes.map((attr) => attr.id) : [],
      });
    } else {
      setEditing(null);
      form.resetFields();
      form.setFieldsValue({ isActive: true, isOther: false, attributeIds: [] });
    }
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: ControlAreaPayload = {
        ...values,
        name: (values?.name || '').toLocaleUpperCase('tr-TR'),
        isActive: values.isActive !== false,
        isOther: values.isOther === true,
        attributeIds: Array.isArray(values.attributeIds)
          ? values.attributeIds
              .map((id: any) => Number(id))
              .filter((id: number) => Number.isFinite(id))
          : [],
      };
      if (editing) {
        await ControlAreaService.updateControlArea(editing.id, payload);
        message.success('Tip - Özellik güncellendi.');
      } else {
        await ControlAreaService.createControlArea(payload);
        message.success('Tip - Özellik oluşturuldu.');
      }
      handleCloseDrawer();
      fetchControlAreas();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      const serverMessage = error?.response?.data?.message;
      message.error(serverMessage || 'İşlem sırasında bir hata oluştu.');
    }
  };

  const handleDelete = async (record: ControlAreaResponse) => {
    try {
      await ControlAreaService.deleteControlArea(record.id);
      message.success('Tip - Özellik silindi.');
      fetchControlAreas();
    } catch (error: any) {
      const serverMessage = error?.response?.data?.message;
      message.error(serverMessage || 'Tip - Özellik silinemedi.');
    }
  };

  const handleStatusToggle = async (record: ControlAreaResponse, nextValue: boolean) => {
    if (typeof record?.id !== 'number') return;
    try {
      setStatusUpdatingId(record.id);
      const payload: ControlAreaPayload = {
        name: record.name,
        isActive: nextValue,
        isOther: record.isOther === true,
        attributeIds: Array.isArray(record.attributes)
          ? record.attributes
              .map((attr) => Number(attr?.id))
              .filter((id: number) => Number.isFinite(id))
          : [],
      };
      await ControlAreaService.updateControlArea(record.id, payload);
      message.success(nextValue ? 'Tip - Özellik aktif edildi.' : 'Tip - Özellik pasif edildi.');
      fetchControlAreas();
    } catch (error: any) {
      const serverMessage = error?.response?.data?.message;
      message.error(serverMessage || 'Durum güncellenemedi.');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleOtherToggle = async (record: ControlAreaResponse, nextValue: boolean) => {
    if (typeof record?.id !== 'number') return;
    try {
      setStatusUpdatingId(record.id);
      const payload: ControlAreaPayload = {
        name: record.name,
        isActive: record.isActive !== false,
        isOther: nextValue,
        attributeIds: Array.isArray(record.attributes)
          ? record.attributes
              .map((attr) => Number(attr?.id))
              .filter((id: number) => Number.isFinite(id))
          : [],
      };
      await ControlAreaService.updateControlArea(record.id, payload);
      message.success('Diğer ayarı güncellendi.');
      fetchControlAreas();
    } catch (error: any) {
      const serverMessage = error?.response?.data?.message;
      message.error(serverMessage || 'Diğer ayarı güncellenemedi.');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const totalCount = useMemo(() => filtered.length, [filtered]);

  return (
    <Card
      className="br-30px mb-60px kontrolx-card"
      title={(
        <Row align="middle">
          <Tabs
            activeKey={statusTab}
            onChange={(key) => setStatusTab(key === 'passive' ? 'passive' : 'active')}
            className="custom-tabs"
          >
            <Tabs.TabPane
              key="active"
              tab={(
                <Button type={statusTab === 'active' ? 'primary' : 'default'} className="d-button tab-button">
                  Aktif Parametre Tip-Özellik Listesi
                </Button>
              )}
            />
            <Tabs.TabPane
              key="passive"
              tab={(
                <Button type={statusTab === 'passive' ? 'primary' : 'default'} className="d-button tab-button">
                  Pasif Parametre Tip-Özellik Listesi
                </Button>
              )}
            />
          </Tabs>
          <Text type="secondary" className="ms-5" style={{ fontWeight: 500, marginTop: 24 }}>
            <strong>({totalCount})</strong> kayıt listelendi
          </Text>
        </Row>
      )}
      extra={
        <Row align="middle">
              <Input
                allowClear
                placeholder="Ara"
                prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
                className="d-input d-input-form d-input-filter"
                style={{ width: 200 }}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              <Button
                type="primary"
                className="d-button width-auto ml-10px dark-button"
                onClick={() => { setPendingAttributeIds(selectedAttributeIds); setFilterOpen(true); }}
              >
                Filtrele
              </Button>
              <Button
                type="primary"
                className="d-button width-auto ml-10px dark-button"
                onClick={() => handleOpenDrawer()}
              >
                Yeni Tip - Özellik Ekle
              </Button>
            </Row>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={filtered}
          pagination={false}
          columns={[
            {
              title: 'Tip - Özellik',
              dataIndex: 'name',
              key: 'name',
              sorter: (a: ControlAreaResponse, b: ControlAreaResponse) =>
                a.name.localeCompare(b.name, 'tr'),
              sortDirections: ['ascend', 'descend'],
            },
            {
              title: 'Parametreler',
              dataIndex: 'attributes',
              key: 'attributes',
              render: (_: any, record: ControlAreaResponse) => {
                const attrs = Array.isArray(record.attributes) ? record.attributes : [];
                if (!attrs.length) {
                  return <Typography.Text type="secondary">-</Typography.Text>;
                }
                return (
                  <Space size={[4, 4]} wrap>
                    {attrs.map((attr, index) => (
                      <Tag key={attr?.id ?? `${record.id}-${index}`}>
                        {attr?.name || (attr?.id ? `#${attr.id}` : '-')}
                      </Tag>
                    ))}
                  </Space>
                );
              },
            },
            {
              title: 'Durum',
              dataIndex: 'isActive',
              key: 'isActive',
              width: 140,
              render: (value: boolean, record: ControlAreaResponse) => (
                <Switch
                  checked={value !== false}
                  checkedChildren="Aktif"
                  unCheckedChildren="Pasif"
                  loading={statusUpdatingId === record.id}
                  onChange={(checked) => handleStatusToggle(record, checked)}
                />
              ),
            },
            {
              title: 'Diğer',
              dataIndex: 'isOther',
              key: 'isOther',
              width: 120,
              render: (value: boolean, record: ControlAreaResponse) => (
                <Switch
                  checked={value === true}
                  checkedChildren="Evet"
                  unCheckedChildren="Hayır"
                  loading={statusUpdatingId === record.id}
                  onChange={(checked) => handleOtherToggle(record, checked)}
                />
              ),
            },
            {
              title: 'İşlemler',
              key: 'actions',
              width: 140,
              render: (_: any, record: ControlAreaResponse) => (
                <Space size={12}>
                  <Tooltip title="Düzenle">
                    <Button
                      shape="circle"
                      className="icon-button"
                      onClick={() => handleOpenDrawer(record)}
                      icon={<img src={PenIcon} alt="pen" className="icon" />}
                    />
                  </Tooltip>
                  <Popconfirm
                    title="Bu Tip - Özellik kaydını silmek istediğinize emin misiniz?"
                    okText="Evet"
                    cancelText="Hayır"
                    onConfirm={() => handleDelete(record)}
                  >
                    <Tooltip title="Sil">
                      <Button
                        shape="circle"
                        className="icon-button ml-12px"
                        icon={<img src={TrashIcon} alt="trash" className="icon" />}
                      />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              ),
            },
            ]}
        />
      </Space>

      <Drawer
        title={editing ? 'Tip - Özelliği Düzenle' : 'Yeni Tip - Özellik'}
        open={drawerOpen}
        onClose={handleCloseDrawer}
        destroyOnClose
        width={400}
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={handleCloseDrawer}>İptal</Button>
            <Button type="primary" onClick={handleSubmit}>
              Kaydet
            </Button>
          </Space>
        }
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="name"
            label="Tip - Özellik"
            rules={[
              { required: true, message: 'Tip - Özellik zorunludur.' },
              { max: 255, message: 'Tip - Özellik 255 karakterden uzun olamaz.' },
            ]}
          >
            <Input
              placeholder="Tip - Özellik giriniz"
              maxLength={255}
              onChange={(e) => {
                const upper = (e.target.value || '').toLocaleUpperCase('tr-TR');
                form.setFieldsValue({ name: upper });
              }}
            />
          </Form.Item>
          <Form.Item name="attributeIds" label="Parametreler">
            <Select
              mode="multiple"
              allowClear
              placeholder="Parametre seçiniz"
              options={attributeOptions}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))
              }
            />
          </Form.Item>
          <Form.Item name="isActive" label="Aktif/Pasif" valuePropName="checked">
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>
          <Form.Item
            name="isOther"
            label="Diğer"
            valuePropName="checked"
            extra="Birden fazla Tip - Özellik kaydı Diğer olarak işaretlenebilir."
          >
            <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
          </Form.Item>
        </Form>
      </Drawer>
      <Drawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        destroyOnClose
        width={420}
        title="Filtre"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Typography.Text>Parametreler</Typography.Text>
            <Select
              mode="multiple"
              allowClear
              placeholder="Seçiniz"
              style={{ width: '100%', marginTop: 8 }}
              value={pendingAttributeIds}
              onChange={(vals: number[]) => setPendingAttributeIds(vals)}
              options={attributeOptions}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))
              }
            />
          </div>
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button
              onClick={() => {
                setPendingAttributeIds([]);
                setSelectedAttributeIds([]);
              }}
            >
              Temizle
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setSelectedAttributeIds(pendingAttributeIds);
                setFilterOpen(false);
              }}
            >
              Filtrele
            </Button>
          </Space>
        </Space>
      </Drawer>
    </Card>
  );
};

export default ControlAreaList;
